import {
    getFileName,
    logAssetDownload
} from '../../ItemDataCategoryTable/ItemDataCategoryTableUtils';
import { Trans } from '@lingui/react';
import filesize from 'filesize';
import LimitText from '../../../utils/LimitText';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getAssetURL } from '../../../../api';
import { useNewAssetPreview } from '../../../../hooks/useNewAssetPreview';
import { getDocumentFileIcon, getExternalLinkIcon, getFileExtension, isDocumentFile, isUrlAsset } from '../../../../helpers';
import { selectors } from '../../../../reducers/appReducer';
import noPhotoImg from '../../../../images/no-photo.png';

const ArticleAssetPreviewCard = ({item, clickCallback, articleDetails = null, locale = 'it_IT'}) => {

    const { singlePreview, loading } = useNewAssetPreview(item)

    // Determina se l'asset è un URL esterno (non un file con URL)
    // Usa item.extension per determinare se è un file (pdf, jpg, etc.) o un URL (null o 'url')
    const isExternalUrl = (isUrlAsset(item.public_url) && !item.extension) || item.extension === 'url'
    const ExternalLinkIcon = getExternalLinkIcon()

    const imagePlaceholder = useSelector(state => selectors.getDefaultPlaceholderImage(state)?.imageUrl);
    const imagePlaceholderUrl = imagePlaceholder === './images/no-photo.png' ? noPhotoImg : (imagePlaceholder || noPhotoImg);

    const displayFileName = useMemo(() => {
        return getFileName(item.filename ?? item.url)
    }, [item.filename, item.url])

    const description = useMemo(() => {
        if (item.description === '-') return ''
        return item.description?.[locale] ?? item.description?.it_IT ?? item.description ?? ''
    }, [item.description, locale])

    // Handler per il click sulla card - per URL esterni apre il link
    const handleCardClick = () => {
        if (isExternalUrl) {
            window.open(item.public_url, '_blank', 'noopener,noreferrer')
        } else {
            clickCallback(item)
        }
    }

    return (
        <div key={item.id} className="p-2 bg-slate-200 rounded">
            <div
                onClick={handleCardClick}
                className="flex items-center cursor-pointer min-h-[140px] justify-center">
                {/* URL esterno - mostra icona link */}
                {isExternalUrl && (
                    <ExternalLinkIcon
                        size={48}
                        className="!text-primary !hover:text-gray-700 transition-colors align-center"
                    />
                )}

                {/* File documento - mostra icona documento */}
                {!isExternalUrl && isDocumentFile(item?.filename ?? item?.url) && (() => {
                    const Icon = getDocumentFileIcon(item?.filename ?? item?.url);

                    return (
                        <Icon
                            size={48}
                            className="!text-primary !hover:text-gray-700 transition-colors align-center"
                        />
                    );
                })()}

                {/* File immagine normale - mostra preview */}
                {!isExternalUrl && !isDocumentFile(item?.filename ?? item?.url) && (
                    <img
                        className="img-responsive"
                        src={getAssetURL(singlePreview)}
                        alt={item.url}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null; // prevents looping
                            currentTarget.src = imagePlaceholderUrl;
                        }}
                        style={{
                            maxHeight: '140px',
                            margin: 'auto auto',
                        }}
                    />
                )}
            </div>
            <div className="m-0">
                <div
                    className="text-primary"
                    title={item.filename ?? getFileName(item.url)}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {displayFileName}
                </div>
                <div className="flex">
                    <div className="grow">
                        <span>
                            {isExternalUrl ? (
                                <span title={item.public_url}>
                                    URL: {(() => {
                                        const cleanUrl = item.public_url?.replace(/^https?:\/\//, '') || '';
                                        return cleanUrl.length > 10 ? cleanUrl.slice(0, 10) + '...' : cleanUrl;
                                    })()}
                                </span>
                            ) : (
                                <>
                                    <Trans id="ext" />: {item?.ext ?? getFileExtension(item?.filename)}
                                    <br />
                                    {item.size && (
                                        <>
                                            <Trans id="weight" />:{' '}
                                            {filesize(item.size, {
                                                separator: ',',
                                                base: 10,
                                            })}
                                        </>
                                    )}
                                </>
                            )}
                        </span>
                    </div>
                    <div>
                        {isExternalUrl ? (
                            <a
                                className="btn btn-primary"
                                title="Apri link"
                                href={item.public_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <ExternalLinkIcon />
                            </a>
                        ) : (
                            <a
                                className="btn btn-primary"
                                title="Download"
                                href={getAssetURL(encodeURIComponent(item.url))}
                                onClick={() => logAssetDownload(articleDetails, item)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="icon icon-download" />
                            </a>
                        )}
                    </div>
                </div>
                <div className="text-bold">
                    <LimitText
                        limit={20}
                        text={description}
                    />
                </div>
            </div>
        </div>

    )
}

export default ArticleAssetPreviewCard